import { useEffect, useState } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import { App, Avatar, Dropdown, Flex, Progress, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import InputComponent from "../../Components/Input"
import Private, { INCOGNITO_MASK } from "../../Components/Private"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import {
  getProgressData,
  getProgressPercent,
  isAllBusinessCompleted,
  isStepCompleted
} from "../../Utils/onboarding/calculateProgress"
import { getSwitcherAccounts } from "../../Utils/onboarding/getSwitcherAccounts"
import { isFirstBAInvitedUser } from "../../Utils/onboarding/isFirstBAInvitedUser"
import { isOnboardingFinished as getIsOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { createAccountStepPath } from "../../Utils/utils"
import AlertsActions from "../AlertsPage/reducer"
import BalanceActions from "../Balance/reducer"
import CreditsActions from "../CreditsPage/reducer"
import GettingStartedProgressIcon from "../GettingStartedPage/componets/GettingStartedProgressIcon"
import GlobalFiltersActions from "../GlobalFilters/reducer"
import InvoicesActions from "../InvoiceHistoryBlock/reducer"
import { useSwitchBusinessAccount } from "../User/hooks/useSwitchBusinessAccount"

import "./topSidebarMenu.less"

const { Text, Paragraph } = Typography

export const TopSideBarMenu = ({ ...props }) => {
  const { theme, isShowSideBar } = useSelector((state) => state.nav)
  const incognito = useIncognito()
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const externalDemoMode = useExternalDemoMode()

  const [search, setSearch] = useState("")
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)

  const {
    businessAccountsWithRole,
    businessAccountsFetching,
    businessAccountSwitching,
    businessAccounts,
    saveResult,
    nextCreateAccountStep
  } = useSelector((state) => state.user)
  const { userInfo, isAdmin } = useSelector((state) => state.auth)
  const isOnboardingFinished = getIsOnboardingFinished(userInfo)
  const isNotInvitedUser = !userInfo?.is_created_by_invitation
  const baCount = businessAccountsWithRole?.length || 0
  let businessId = userInfo?.["current_business_account_id"]
  const activeAccount = businessAccountsWithRole?.filter((el) => el?.id === businessId)?.[0] || {
    name: null,
    id: null
  }
  const isInvitedWithoutRole = userInfo?.is_created_by_invitation && !!userInfo?.role
  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content", width: 475 }
  }
  const isFirstBAInvetedUser = isFirstBAInvitedUser(businessAccountsWithRole)
  const isAdditionalStep =
    isFirstBAInvetedUser && userInfo.is_created_by_invitation && !userInfo.new_onboarding_finished

  const { switchBA, goToIncompleteBAOnboarding, incompleteBA } = useSwitchBusinessAccount()

  const resumeEntryBA = isAdditionalStep ? null : incompleteBA
  const filteredBusinessAccounts = getSwitcherAccounts(
    businessAccountsWithRole,
    resumeEntryBA
  )?.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()))
  const shouldShowAvatarForBa = (ba) => {
    if (isAdditionalStep) {
      return isAdditionalStep && ba.role.name !== "Owner" && isStepCompleted(ba.completed_steps)
    }
    return isStepCompleted(ba.completed_steps)
  }

  useEffect(() => {
    if (nextCreateAccountStep && !isInvitedWithoutRole) {
      props.getBusinessAccounts()
    }
  }, [location?.pathname, nextCreateAccountStep])

  const resetFilters = () => {
    dispatch(AlertsActions.clearAlertsFilters())
    dispatch(CreditsActions.clearCreditsFilters())
    dispatch(GlobalFiltersActions.clearGlobalFilters())
    dispatch(BalanceActions.clearBalanceFilters())
    dispatch(InvoicesActions.clearInvoicesFilters())
  }

  useEffect(() => {
    if (saveResult) {
      if (saveResult === "business_created" && baCount > 1) {
        notification.success({
          message: t("Business created"),
          description: t("Your business has been successfully created."),
          ...notificationConfig
        })
        resetFilters()
        // props.userSaveResultReset();
      }
    }
  }, [saveResult])

  const handleAddNewAccount = () => {
    props.userSaveResultReset()
    props.changeNextCreateAccountStep(1)
    props.setRedirectAfterCreateAccount(location?.pathname)
    navigate(createAccountStepPath[7])
    if (!isInvitedWithoutRole) props.getBusinessAccounts()
  }

  const handleChangeSearch = (e) => {
    const value = e.target.value
    setSearch(value)
  }

  const handleVisibleChange = (value) => {
    setIsDropdownVisible(value)
    // setSearch('')
  }

  const getAvatar = (businessAccount, width) => (
    <Avatar
      className="user-avatar"
      size={width ? width : 24}
      style={{
        backgroundColor: businessAccount?.photo ? "transparent" : "#225AEA",
        borderRadius: "8px",
        border: "none",
        fontSize: 14,
        minWidth: 24
      }}
      {...(businessAccount?.photo && { src: businessAccount?.photo })}
    >
      {!businessAccount?.photo && !incognito ? businessAccount?.name?.[0] || "B" : null}
    </Avatar>
  )

  const onChangeBusinessAccount = (ba) => {
    if (businessAccountSwitching) return

    // If user clicks on active ba
    if (ba.id === businessId) {
      const { done, total } = getProgressData(activeAccount?.completed_steps)
      if (done < total || (ba?.role?.name === "Owner" && isAdditionalStep)) {
        props.changeNextCreateAccountStep(done + 1)
        props.setRedirectAfterCreateAccount(location.pathname)
        props.getBusinessAccounts()
        navigate(createAccountStepPath[7])
      }
      return
    }

    // If user clicks on different ba
    switchBA(ba.id)
  }

  const handleMenuClick = ({ key, domEvent }) => {
    domEvent?.stopPropagation()

    if (key === "add_account") {
      if (!externalDemoMode) handleAddNewAccount()
      return
    }

    if (key === "resume_account") {
      if (!externalDemoMode) goToIncompleteBAOnboarding()
      return
    }

    if (key === "search") return

    const selectedBusinessAccount = filteredBusinessAccounts?.find(
      (ba) => String(ba.id) === String(key)
    )

    if (selectedBusinessAccount) {
      onChangeBusinessAccount(selectedBusinessAccount)
    }
  }

  return (
    <Dropdown
      overlayClassName={"custom-menu side-bar-menu"}
      getPopupContainer={() => document.getElementById("main-layout")}
      overlayStyle={{ width: 300 }}
      disabled={businessAccountSwitching}
      onOpenChange={isShowSideBar ? handleVisibleChange : null}
      open={isDropdownVisible}
      menu={{
        onClick: handleMenuClick,
        items: [
          baCount > 5 && {
            key: "search",
            disabled: true,
            label: (
              <div className="dropdown-search-wrapper">
                <InputComponent
                  className="dropdown-search"
                  prefix={<Icon type="search" role="icon" />}
                  placeholder={`${t("Search")}...`}
                  value={search}
                  onChange={handleChangeSearch}
                />
              </div>
            )
          },
          ...filteredBusinessAccounts?.map((ba) => ({
            key: ba.id,
            label: (
              <Flex align="center" gap={4} wrap="nowrap">
                {shouldShowAvatarForBa(ba) ? (
                  getAvatar(ba)
                ) : (
                  <>
                    <Progress
                      type="circle"
                      percent={getProgressPercent(ba.completed_steps, isAdditionalStep)}
                      width={20}
                      showInfo={false}
                      strokeColor="#225AEA"
                    />
                  </>
                )}
                {isShowSideBar || (!isShowSideBar && isDropdownVisible) ? (
                  <>
                    <Paragraph
                      ellipsis={{
                        rows: 1,
                        expandable: true,
                        tooltip: incognito ? INCOGNITO_MASK : ba.name,
                        symbol: null
                      }}
                      style={{ margin: 0 }}
                    >
                      <Private>{ba.name}</Private>
                    </Paragraph>
                    {ba.role ? (
                      <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {ba?.role?.name}
                      </Text>
                    ) : null}
                    <Flex align="center" justify="center" style={{ marginLeft: "auto" }}>
                      {activeAccount.id === ba.id && (
                        <Icon type="check_markblue" style={{ fontSize: 16, margin: "0 auto" }} />
                      )}
                    </Flex>
                  </>
                ) : null}
              </Flex>
            )
          })),
          // The last entry either starts a new business or, while one is still being set up,
          // names it and resumes that setup (OB-3-15, OB-3-16).
          (isShowSideBar || (!isShowSideBar && isDropdownVisible)) && !isAdditionalStep
            ? resumeEntryBA
              ? {
                  key: "resume_account",
                  label: (
                    <Flex align="center" gap={4} wrap="nowrap">
                      <Progress
                        type="circle"
                        percent={getProgressPercent(resumeEntryBA.completed_steps)}
                        width={24}
                        showInfo={false}
                        strokeColor="#225AEA"
                        style={{ marginRight: 12 }}
                      />
                      <Paragraph
                        ellipsis={{
                          rows: 1,
                          tooltip: incognito ? INCOGNITO_MASK : resumeEntryBA.name,
                          symbol: null
                        }}
                        style={{ margin: 0 }}
                      >
                        <Private>{resumeEntryBA.name}</Private>
                      </Paragraph>
                      <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {t("Finish setup")}
                      </Text>
                    </Flex>
                  )
                }
              : isAllBusinessCompleted(businessAccounts)
                ? {
                    key: "add_account",
                    label: (
                      <Flex align="center">
                        <Icon
                          type={theme === "dark" ? "add_account_dark" : "add_account"}
                          style={{ fontSize: 24, marginRight: 12 }}
                        />
                        <Text type="secondary">{t("Create new business")}</Text>
                      </Flex>
                    )
                  }
                : null
            : null
        ]
      }}
      trigger={["click"]}
      placement="bottomLeft"
    >
      <div
        className={cls(
          "custom-trigger-sidebar",
          { "small-sidebar": !isShowSideBar && !props.isMobile },
          { isMobile: props.isMobile },
          { [theme]: theme }
        )}
        data-tour={"business-switcher"}
        onClick={(e) => e.stopPropagation()}
      >
        <Flex gap={8} align="center">
          {((isOnboardingFinished && isStepCompleted(activeAccount?.completed_steps)) ||
            !isNotInvitedUser) &&
            getAvatar(activeAccount, 36)}
          {!isOnboardingFinished && isNotInvitedUser && <GettingStartedProgressIcon />}
          {!isStepCompleted(activeAccount?.completed_steps) && isNotInvitedUser && (
            <Progress
              type="circle"
              percent={getProgressPercent(activeAccount?.completed_steps)}
              width={20}
              showInfo={false}
              strokeColor="#225AEA"
            />
          )}
          {isShowSideBar ? (
            <Flex vertical gap={0}>
              <Text
                ellipsis={{
                  tooltip: incognito ? INCOGNITO_MASK : activeAccount.name
                }}
                strong
                style={{ whiteSpace: "nowrap" }}
              >
                <Private>{activeAccount?.name}</Private>
              </Text>
              {!props.isMobile && (
                <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                  {baCount > 1 ? "Switch Business" : "Add Business"}
                </Text>
              )}
            </Flex>
          ) : null}
        </Flex>

        {isShowSideBar ? (
          businessAccountsFetching || businessAccountSwitching ? (
            <LoadingOutlined />
          ) : (
            <Icon type="arrows_opposite" />
          )
        ) : null}
      </div>
    </Dropdown>
  )
}
